import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { ChevronLeft, ChevronRight, Clock, Calendar as CalendarIcon, X } from 'lucide-react';
import { overlayStack } from '../../lib/overlayStack';

dayjs.locale('es');

export interface DateTimePickerProps {
  value: string; // ISO 8601
  onChange: (iso: string) => void;
  onClose: () => void;
}

export function DateTimePicker({ value, onChange, onClose }: DateTimePickerProps) {
  // Register with overlayStack for ESC key and body overflow lock
  useEffect(() => {
    const id = overlayStack.push(onClose);
    return () => {
      overlayStack.remove(id);
    };
  }, [onClose]);

  const initialDate = useMemo(() => {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : dayjs();
  }, [value]);

  const [selectedDate, setSelectedDate] = useState(() => initialDate);
  const [viewDate, setViewDate] = useState(() => initialDate);

  const [hoursStr, setHoursStr] = useState(() => String(initialDate.hour()).padStart(2, '0'));
  const [minutesStr, setMinutesStr] = useState(() => String(initialDate.minute()).padStart(2, '0'));

  // Calendar grid calculation
  const calendarDays = useMemo(() => {
    const startOfMonth = viewDate.startOf('month');
    const daysInMonth = viewDate.daysInMonth();
    const startDay = startOfMonth.day(); // 0 = Sunday

    const days = [];

    // Previous month trailing days
    const prevMonth = viewDate.subtract(1, 'month');
    const prevMonthDays = prevMonth.daysInMonth();
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        date: prevMonth.date(prevMonthDays - i),
        isCurrentMonth: false,
      });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({
        date: viewDate.date(d),
        isCurrentMonth: true,
      });
    }

    // Next month leading days
    const totalCells = days.length > 35 ? 42 : 35;
    const remainingCells = totalCells - days.length;
    const nextMonth = viewDate.add(1, 'month');
    for (let d = 1; d <= remainingCells; d++) {
      days.push({
        date: nextMonth.date(d),
        isCurrentMonth: false,
      });
    }

    return days;
  }, [viewDate]);

  const handlePrevMonth = () => {
    setViewDate((prev) => prev.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setViewDate((prev) => prev.add(1, 'month'));
  };

  const handleDaySelect = (date: dayjs.Dayjs) => {
    setSelectedDate(date);
  };

  const handleHoursBlur = () => {
    let num = parseInt(hoursStr, 10);
    if (isNaN(num) || num < 0) num = 0;
    if (num > 23) num = 23;
    setHoursStr(String(num).padStart(2, '0'));
  };

  const handleMinutesBlur = () => {
    let num = parseInt(minutesStr, 10);
    if (isNaN(num) || num < 0) num = 0;
    if (num > 59) num = 59;
    setMinutesStr(String(num).padStart(2, '0'));
  };

  const handleConfirm = () => {
    let h = parseInt(hoursStr, 10);
    if (isNaN(h) || h < 0) h = 0;
    if (h > 23) h = 23;

    let m = parseInt(minutesStr, 10);
    if (isNaN(m) || m < 0) m = 0;
    if (m > 59) m = 59;

    const finalIso = selectedDate
      .hour(h)
      .minute(m)
      .second(0)
      .millisecond(0)
      .toISOString();

    onChange(finalIso);
    onClose();
  };

  const formattedMonthYear = useMemo(() => {
    const str = viewDate.format('MMMM YYYY');
    return str.charAt(0).toUpperCase() + str.slice(1);
  }, [viewDate]);

  const weekDays = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border shadow-2xl rounded-2xl w-full max-w-md p-6 relative flex flex-col space-y-4 animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="datetimepicker-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border/60 pb-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange border border-brand-orange/20">
              <CalendarIcon className="h-5 w-5" />
            </div>
            <h3 id="datetimepicker-title" className="text-base font-black text-text-primary leading-tight">
              Editar Fecha Operativa
            </h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-xl p-2 text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary transition-colors cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Cerrar modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between px-1">
          <button
            onClick={handlePrevMonth}
            className="p-2 rounded-lg hover:bg-surface-secondary text-text-secondary transition-colors"
            aria-label="Mes anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="font-bold text-sm text-text-primary">
            {formattedMonthYear}
          </span>
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-lg hover:bg-surface-secondary text-text-secondary transition-colors"
            aria-label="Mes siguiente"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Days of Week Header */}
        <div className="grid grid-cols-7 gap-1 text-center">
          {weekDays.map((day) => (
            <div key={day} className="text-xs font-semibold text-text-tertiary uppercase py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((cell, idx) => {
            const isSelected = cell.date.isSame(selectedDate, 'day');
            const isToday = cell.date.isSame(dayjs(), 'day');

            let dayClasses = 'h-10 w-full rounded-xl text-sm font-medium flex items-center justify-center transition-all cursor-pointer ';

            if (isSelected) {
              dayClasses += 'bg-brand-sky text-white font-bold shadow-md shadow-brand-sky/20';
            } else if (isToday) {
              dayClasses += 'ring-1 ring-brand-sky text-brand-sky font-semibold hover:bg-surface-secondary';
            } else if (!cell.isCurrentMonth) {
              dayClasses += 'text-text-tertiary/40 hover:bg-surface-secondary hover:text-text-secondary';
            } else {
              dayClasses += 'text-text-primary hover:bg-surface-secondary hover:text-brand-sky';
            }

            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleDaySelect(cell.date)}
                className={dayClasses}
              >
                {cell.date.date()}
              </button>
            );
          })}
        </div>

        {/* Time Selector */}
        <div className="flex items-center justify-between bg-surface-secondary/50 p-3 rounded-xl border border-border/60">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-brand-sky shrink-0" />
            <span className="text-sm font-bold text-text-primary">Hora:</span>
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={23}
              value={hoursStr}
              onChange={(e) => setHoursStr(e.target.value)}
              onBlur={handleHoursBlur}
              className="w-14 px-2 py-1.5 bg-surface border border-border rounded-lg text-center font-mono font-bold text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-sky"
            />
            <span className="font-bold text-text-secondary">:</span>
            <input
              type="number"
              min={0}
              max={59}
              value={minutesStr}
              onChange={(e) => setMinutesStr(e.target.value)}
              onBlur={handleMinutesBlur}
              className="w-14 px-2 py-1.5 bg-surface border border-border rounded-lg text-center font-mono font-bold text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-sky"
            />
          </div>
        </div>

        {/* Footer / Confirm Button */}
        <button
          type="button"
          onClick={handleConfirm}
          className="w-full py-3 px-4 rounded-xl bg-brand-orange hover:bg-brand-orange/90 text-white font-extrabold text-sm transition-all shadow-md active:scale-[0.98] cursor-pointer"
        >
          Confirmar Fecha
        </button>
      </div>
    </div>
  );
}
